if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.cartLimitVariantId = null;
        this.cartLimitQuantity = 0;
        this.cartLimitOriginalText = '';
        this.cartUpdateUnsubscriber = null;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      connectedCallback() {
        if (
          !this.cartUpdateUnsubscriber &&
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.cartUpdate
        ) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
        }
      }

      disconnectedCallback() {
        if (typeof this.cartUpdateUnsubscriber === 'function') this.cartUpdateUnsubscriber();
        this.cartUpdateUnsubscriber = null;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const cartQuantityBefore = this.getKnownCartQuantity(variantId);
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              const errorMessage = response.description || response.message;
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(errorMessage);
              this.dispatchCartErrorEvent(errorMessage, 'INVALID');

              const reconciledState = await this.fetchReconciledCartState(variantId);
              const cartQuantityAfter = this.getVariantCartQuantity(reconciledState, variantId);

              if (cartQuantityAfter > cartQuantityBefore) {
                this.resolveCartLinesUpdate(linesUpdateDeferred, reconciledState);
                this.error = false;

                if (!this.cart) {
                  window.location = window.routes.cart_url;
                  return;
                }

                await this.renderReconciledCartContents(reconciledState, variantId);
                return;
              }

              linesUpdateDeferred?.reject(new Error(errorMessage));

              if (this.isCartLimitError(errorMessage) && cartQuantityAfter > 0) {
                this.setCartLimitState(variantId, cartQuantityAfter);
                this.error = true;
                return;
              }

              if (this.isSoldOutError(errorMessage)) {
                const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
                if (soldOutMessage) {
                  this.submitButton.setAttribute('aria-disabled', true);
                  this.submitButtonText.classList.add('hidden');
                  soldOutMessage.classList.remove('hidden');
                  this.error = true;
                  return;
                }
              }

              this.error = false;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            this.renderCartContents(response);
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      getKnownCartQuantity(variantId) {
        const productQuantity = this.closest('product-info')?.querySelector('.product-form__quantity .quantity__input');
        const productCartQuantity = parseInt(productQuantity?.dataset.cartQuantity, 10) || 0;
        const renderedCartQuantity = [...document.querySelectorAll('[data-quantity-variant-id]')].reduce((total, input) => {
          if (String(input.dataset.quantityVariantId) !== String(variantId)) return total;
          return total + (parseInt(input.value, 10) || 0);
        }, 0);

        return Math.max(productCartQuantity, renderedCartQuantity);
      }

      getVariantCartQuantity(cartData, variantId) {
        return (cartData?.items || []).reduce((total, item) => {
          if (String(item.variant_id) !== String(variantId)) return total;
          return total + (parseInt(item.quantity, 10) || 0);
        }, 0);
      }

      fetchReconciledCartState(variantId) {
        return fetch(`${routes.cart_url}.json`, {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        })
          .then((response) => {
            if (!response.ok) throw new Error(`Cart reconciliation failed (${response.status})`);
            return response.json();
          })
          .then((cartData) => ({
            ...cartData,
            id: parseInt(variantId, 10) || variantId,
          }));
      }

      async renderReconciledCartContents(cartData, variantId) {
        if (typeof this.cart?.refreshAfterCartMutation === 'function') {
          await this.cart.refreshAfterCartMutation(cartData, variantId);
          return;
        }

        // A partial inventory mutation must never be rendered from synthetic or
        // stale section HTML. If the active cart surface cannot perform an
        // authoritative server refresh, publish the real cart state and use the
        // canonical cart page as the safe fallback.
        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId: variantId,
          cartData,
        });
        CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
        window.location = window.routes.cart_url;
      }

      renderCartContents(response) {
        if (!this.cart) return;

        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => {
                CartPerformance.measure('add:paint-updated-sections', () => {
                  this.cart.renderContents(response);
                });
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
        } else {
          CartPerformance.measure('add:paint-updated-sections', () => {
            this.cart.renderContents(response);
          });
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      isCartLimitError(errorMessage) {
        const message = String(errorMessage || '').toLocaleLowerCase();
        return [
          "can't add more",
          'cannot add more',
          'can’t add more',
          'you can only add',
          'maximum quantity',
          'max quantity',
          'no puedes agregar más',
          'no se puede agregar más',
          'no se pueden agregar más',
          'cantidad máxima',
          'máximo en el carrito',
        ].some((fragment) => message.includes(fragment));
      }

      isSoldOutError(errorMessage) {
        const message = String(errorMessage || '').toLocaleLowerCase();
        return [
          'sold out',
          'is not available',
          'not enough items',
          'agotado',
          'no está disponible',
          'no esta disponible',
          'no hay suficientes',
        ].some((fragment) => message.includes(fragment));
      }

      setCartLimitState(variantId, cartQuantity) {
        const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
        if (!this.cartLimitOriginalText) {
          this.cartLimitOriginalText = this.submitButtonText?.textContent?.trim() || window.variantStrings.addToCart;
        }

        this.cartLimitVariantId = String(variantId);
        this.cartLimitQuantity = parseInt(cartQuantity, 10) || 0;
        this.dataset.egCartLimit = 'true';
        this.submitButton.dataset.egCartLimit = 'true';
        soldOutMessage?.classList.add('hidden');
        this.submitButtonText?.classList.remove('hidden');
        if (this.submitButtonText) this.submitButtonText.textContent = 'MÁXIMO EN CARRITO';
        this.submitButton.setAttribute('aria-disabled', true);
      }

      clearCartLimitState() {
        if (!this.cartLimitVariantId) return;

        const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
        soldOutMessage?.classList.add('hidden');
        this.submitButtonText?.classList.remove('hidden');
        if (this.submitButtonText) {
          this.submitButtonText.textContent = this.cartLimitOriginalText || window.variantStrings.addToCart;
        }
        this.submitButton.removeAttribute('aria-disabled');
        delete this.dataset.egCartLimit;
        delete this.submitButton.dataset.egCartLimit;
        this.cartLimitVariantId = null;
        this.cartLimitQuantity = 0;
        this.cartLimitOriginalText = '';
        this.error = false;
        this.handleErrorMessage();
      }

      async onCartUpdate(event = {}) {
        if (!this.cartLimitVariantId) return;

        let cartData = event.cartData;
        if (!Array.isArray(cartData?.items)) {
          try {
            cartData = await this.fetchReconciledCartState(this.cartLimitVariantId);
          } catch (error) {
            return;
          }
        }

        const currentQuantity = this.getVariantCartQuantity(cartData, this.cartLimitVariantId);
        if (currentQuantity < this.cartLimitQuantity) this.clearCartLimitState();
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred, cartData = null) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = cartData
          ? Promise.resolve(cartData)
          : typeof CartItems !== 'undefined'
            ? CartItems.fetchCartData()
            : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
